import React, { memo, ReactElement, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { SpinnerBlock } from 'Components/SpinnerBlock';
import { refreshCompanyFeatureFlags } from 'Containers/User/actions';
import { userFeatureFlagsSelector, userFeatureFlagsStatusSelector } from 'Containers/User/selector';

interface Props {
  legacy: ReactElement | null;
  serialized: ReactElement | null;
}

/**
 * Renders the serialized equipment tree when the company's `serializedEquipment` flag is ON,
 * and the legacy count-based tree when OFF.
 *
 * Gated on the feature-flag request status so we never guess the mode before it is known:
 *  - `idle` / `loading` → spinner (NEITHER tree mounts). Feature flags default to `false`
 *    before the API responds, so gating on the flag value alone would briefly mount the
 *    legacy tree — and fire its legacy requests — for an ON company, then swap.
 *  - `failed` → retryable error state. We do NOT fall back to legacy: a serialized company
 *    must not silently show the legacy equipment UI just because the flags request failed.
 *  - `loaded` → gate on the real flag value.
 *
 * The legacy/serialized subtrees are passed in as elements so the unselected branch never
 * mounts (and its effects never run). See WEBAPP-FR-006.
 */
function EquipmentModeBoundary({ legacy, serialized }: Props) {
  const dispatch = useDispatch();
  const status = useSelector(userFeatureFlagsStatusSelector, areEqual);
  const { serializedEquipment } = useSelector(userFeatureFlagsSelector, areEqual);

  // Retry re-runs the FULL active-company sync → feature-flags fetch (not just the flag GET), so a
  // sync failure that triggered this error state can actually recover and flags resolve for the
  // correct company.
  const onRetry = useCallback(() => {
    dispatch(refreshCompanyFeatureFlags());
  }, [dispatch]);

  if (status === 'failed') {
    return (
      <div className="d-flex flex-column align-items-center p-4" role="alert" data-testid="equipment-mode-error">
        <p className="text-muted mb-3">Couldn’t load equipment right now.</p>
        <button type="button" className="btn btn-outline-primary" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (status !== 'loaded') {
    return <SpinnerBlock fetching />;
  }

  return serializedEquipment ? serialized : legacy;
}

const EquipmentModeBoundaryMemo = memo(EquipmentModeBoundary, areEqual);

// One boundary per equipment surface so each surface gates itself independently and legacy
// effects never mount while ON. Same logic today; kept as distinct names so the project
// (RocketDry) boundary can diverge from the company one later without a rename churn.
export {
  EquipmentModeBoundaryMemo as EquipmentInventoryModeBoundary,
  EquipmentModeBoundaryMemo as RocketDryEquipmentModeBoundary,
};
